import { ChevronRight, Copy, Download, Info, Minus, Plus, RotateCcw, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { href } from '../app/router';
import { MicTest } from '../components/MicTest';
import { Sheet } from '../components/Sheet';
import { toast } from '../components/Toast';
import { allItems, course } from '../content';
import { HABITS } from '../lib/habit';
import type { ThemePreference } from '../state/model';
import type { Goal } from '../content/types';
import { exportData, resetProgress, setGoal, setHabit, setSettings, unflag, useAppState } from '../state/store';

function Stepper({ value, min, max, onChange, label }: { value: number; min: number; max: number; onChange: (v: number) => void; label: string }) {
  return (
    <div className="stepper" role="group" aria-label={label}>
      <button type="button" className="icon-btn" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} aria-label={`Fewer: ${label}`}>
        <Minus size={18} aria-hidden="true" />
      </button>
      <output aria-live="polite">{value}</output>
      <button type="button" className="icon-btn" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} aria-label={`More: ${label}`}>
        <Plus size={18} aria-hidden="true" />
      </button>
    </div>
  );
}

function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: Array<{ value: T; label: string }>; onChange: (v: T) => void; label: string }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

const FLAG_LABEL = { greek: 'Greek', meaning: 'Meaning', audio: 'Audio', note: 'Note', other: 'Other' };

export function Settings() {
  const { settings, profile } = useAppState();
  const [confirmReset, setConfirmReset] = useState(false);
  const [habitOpen, setHabitOpen] = useState(false);
  const [habitDraft, setHabitDraft] = useState(profile.habit ?? '');
  const flags = Object.entries(profile.flags);

  const download = async () => {
    const json = await exportData();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `learning-greek-progress-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const copyFlags = async () => {
    const text = flags
      .map(([id, f]) => {
        const item = allItems.find((i) => i.id === id);
        return `${id}\t${item?.text ?? ''}\t${FLAG_LABEL[f.reason]}\t${f.at.slice(0, 10)}${f.note ? `\t${f.note}` : ''}`;
      })
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast('Flag list copied.');
    } catch {
      toast("Couldn't copy here. Use Export instead.");
    }
  };

  return (
    <main className="page" id="main">
      <header className="stack">
        <p className="eyebrow">Settings</p>
        <h1 className="title">Your practice</h1>
      </header>

      <section className="panel" style={{ padding: '4px 20px' }} aria-label="Practice">
        <div className="setting setting-stacked">
          <div className="setting-text">
            <span className="setting-label">Your goal</span>
            <span className="setting-help">Switch any time: both paths start with the same first lessons, so nothing you've learned is lost.</span>
          </div>
          <Segmented<Goal>
            value={profile.goal ?? 'tourist'}
            options={course.tracks.map((t) => ({ value: t.id, label: t.label }))}
            onChange={(v) => setGoal(v)}
            label="Your goal"
          />
        </div>
        <div className="setting">
          <div className="setting-text">
            <span className="setting-label">New phrases a day</span>
            <span className="setting-help">5 keeps reviews to about 10–15 minutes a day.</span>
          </div>
          <Stepper value={settings.newPerDay} min={3} max={10} onChange={(v) => setSettings({ newPerDay: v })} label="New phrases a day" />
        </div>
        <div className="setting">
          <div className="setting-text">
            <span className="setting-label">Weekly target</span>
            <span className="setting-help">Days a week. A target, not a streak.</span>
          </div>
          <Stepper value={settings.weeklyTarget} min={1} max={7} onChange={(v) => setSettings({ weeklyTarget: v })} label="Days a week" />
        </div>
        <button type="button" className="setting" style={{ width: '100%', background: 'none', borderInline: 0, borderBottom: 0, textAlign: 'left', padding: '14px 0' }} onClick={() => { setHabitDraft(profile.habit ?? ''); setHabitOpen(true); }}>
          <div className="setting-text">
            <span className="setting-label">When you practise</span>
            <span className="setting-help">{profile.habit ?? 'Not set'}</span>
          </div>
          <ChevronRight size={20} aria-hidden="true" style={{ color: 'var(--ink-3)' }} />
        </button>
      </section>

      <section className="panel" aria-label="Microphone">
        <MicTest />
      </section>

      <section className="panel" style={{ padding: '4px 20px' }} aria-label="Audio and display">
        <div className="setting">
          <div className="setting-text">
            <span className="setting-label" id="auto-l">
              Stop recording when I stop talking
            </span>
            <span className="setting-help">Otherwise tap to stop.</span>
          </div>
          <button type="button" className="switch" role="switch" aria-checked={settings.autoStop} aria-labelledby="auto-l" onClick={() => setSettings({ autoStop: !settings.autoStop })} />
        </div>
        <div className="setting">
          <div className="setting-text">
            <span className="setting-label" id="respell-l">
              Always show how it sounds
            </span>
            <span className="setting-help">English-letter spelling under the Greek. Otherwise tap the Greek.</span>
          </div>
          <button type="button" className="switch" role="switch" aria-checked={settings.showRespelling} aria-labelledby="respell-l" onClick={() => setSettings({ showRespelling: !settings.showRespelling })} />
        </div>
        <div className="setting" style={{ flexWrap: 'wrap' }}>
          <div className="setting-text">
            <span className="setting-label">Appearance</span>
          </div>
          <Segmented<ThemePreference>
            label="Appearance"
            value={settings.theme}
            onChange={(v) => setSettings({ theme: v })}
            options={[
              { value: 'system', label: 'Auto' },
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
            ]}
          />
        </div>
      </section>

      <section className="stack" aria-labelledby="flags-h">
        <h2 className="section-title" id="flags-h">
          Phrases you flagged
        </h2>
        {flags.length === 0 ? (
          <p className="muted small">None. Use "Flag" on any answer if a phrase looks or sounds wrong.</p>
        ) : (
          <>
            <ul className="phrase-list">
              {flags.map(([id, f]) => {
                const item = allItems.find((i) => i.id === id);
                return (
                  <li key={id} className="phrase-row">
                    <a className="phrase-link" href={href(`/phrase/${id}`)}>
                      <span className="greek greek-body" lang="el">
                        {item?.text ?? id}
                      </span>
                      <span className="small muted">
                        {FLAG_LABEL[f.reason]} · {new Date(f.at).toLocaleDateString('en-GB')}
                        {f.note ? ` · ${f.note}` : ''}
                      </span>
                    </a>
                    <button type="button" className="btn btn-ghost small" onClick={() => unflag(id)}>
                      <Undo2 size={16} aria-hidden="true" /> Withdraw
                    </button>
                  </li>
                );
              })}
            </ul>
            <button type="button" className="btn btn-secondary" style={{ alignSelf: 'flex-start' }} onClick={copyFlags}>
              <Copy size={18} aria-hidden="true" /> Copy the list
            </button>
          </>
        )}
      </section>

      <section className="stack" aria-labelledby="data-h">
        <h2 className="section-title" id="data-h">
          Your data
        </h2>
        <p className="muted small">Everything stays in this browser on this device. Nothing is sent anywhere, and recordings are never saved.</p>
        <div className="row-wrap">
          <button type="button" className="btn btn-secondary" onClick={download}>
            <Download size={18} aria-hidden="true" /> Export progress
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setConfirmReset(true)} style={{ color: 'var(--berry)' }}>
            <RotateCcw size={18} aria-hidden="true" /> Reset progress
          </button>
        </div>
      </section>

      <a href={href('/about')} className="panel-quiet row" style={{ textDecoration: 'none', color: 'inherit' }}>
        <Info size={22} aria-hidden="true" style={{ color: 'var(--aegean)', flex: 'none' }} />
        <span className="stack" style={{ gap: 2, flex: 1 }}>
          <span className="section-title">How this works</span>
          <span className="small muted">The method, the research behind it, and where the audio comes from.</span>
        </span>
        <ChevronRight size={20} aria-hidden="true" style={{ color: 'var(--ink-3)' }} />
      </a>

      <Sheet open={confirmReset} onClose={() => setConfirmReset(false)} title="Reset all progress?">
        <p>Every phrase goes back to new and your history is cleared. Your settings are kept. This can't be undone.</p>
        <div className="stack">
          <button
            type="button"
            className="btn btn-primary btn-block"
            style={{ background: 'var(--berry)' }}
            onClick={async () => {
              await resetProgress();
              setConfirmReset(false);
              toast('Progress reset.');
            }}
          >
            Reset progress
          </button>
          <button type="button" className="btn btn-ghost btn-block" onClick={() => setConfirmReset(false)}>
            Keep my progress
          </button>
        </div>
      </Sheet>

      <Sheet open={habitOpen} onClose={() => setHabitOpen(false)} title="When will you practise?">
        <div className="choice-list" role="radiogroup" aria-label="When will you practise?">
          {HABITS.map((h) => (
            <button key={h} type="button" role="radio" aria-checked={habitDraft === h} className="choice" onClick={() => setHabitDraft(h)}>
              {h}
            </button>
          ))}
        </div>
        <label className="stack" style={{ gap: 6 }}>
          <span className="small muted">Or in your words</span>
          <input className="text-input" value={habitDraft} onChange={(e) => setHabitDraft(e.target.value)} maxLength={60} />
        </label>
        <div className="stack">
          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={async () => {
              await setHabit(habitDraft.trim() || null);
              setHabitOpen(false);
            }}
          >
            Save
          </button>
          <button type="button" className="btn btn-ghost btn-block" onClick={() => setHabitOpen(false)}>
            Cancel
          </button>
        </div>
      </Sheet>
    </main>
  );
}
